import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useEffect, useRef, useState } from "react";

const transport = new DefaultChatTransport({ api: "/api/chat" });

const GREETING: UIMessage = {
  id: "greet-1",
  role: "assistant",
  parts: [
    {
      type: "text",
      text:
        "Hallo, ich bin Angelika – deine digitale Assistentin von Energy Prime 360. Wie darf ich dich ansprechen, und wobei kann ich dir heute helfen?",
    },
  ],
};

function messageText(m: UIMessage) {
  return m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
}

export function AngelikaChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const { messages, sendMessage, status, error } = useChat({
    transport,
    messages: [GREETING],
  });

  const busy = status === "submitted" || status === "streaming";

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, status]);

  // Focus input when opened or after a response
  useEffect(() => {
    if (open && !busy) inputRef.current?.focus();
  }, [open, busy]);

  const submit = () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    void sendMessage({ text });
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Sofort persönlich beraten – Chat mit Angelika starten"
          className="fixed right-5 top-1/2 -translate-y-1/2 z-[100] group"
        >
          <span className="relative inline-flex h-[116px] w-[116px] items-center justify-center sm:h-[128px] sm:w-[128px]">
            <span className="absolute inset-0 rounded-full bg-orange opacity-25 blur-md transition-opacity group-hover:opacity-40" />
            <span className="relative flex h-full w-full items-center justify-center rounded-full bg-orange px-5 text-center text-white shadow-[0_18px_42px_-14px_rgba(0,0,0,0.35)] ring-1 ring-[rgba(0,0,0,0.08)] transition-transform duration-200 group-hover:scale-105">
              <span className="font-display text-[15px] font-semibold leading-[1.15] sm:text-base">
                Sofort persönlich beraten
              </span>
              <span
                className="absolute -bottom-1 right-7 h-5 w-5 rotate-45 rounded-br-sm bg-orange"
                aria-hidden="true"
              />
            </span>
          </span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-4 right-4 z-[100] w-[min(420px,calc(100vw-2rem))] h-[min(640px,calc(100vh-2rem))] rounded-2xl shadow-2xl bg-white border border-[var(--rule)] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between bg-navy text-white px-4 py-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-[var(--leaf)] text-white font-display font-bold shrink-0">
                A
              </span>
              <div className="leading-tight min-w-0">
                <div className="font-display text-sm font-semibold truncate">Angelika</div>
                <div className="text-[11px] opacity-80 truncate">Digitale Assistentin · Chat</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Schließen"
              className="text-xl px-2 py-1 rounded hover:bg-white/10 shrink-0"
            >
              ×
            </button>
          </div>

          {/* Transcript */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-[#f4f6fa] space-y-3">
            {messages.map((m) => {
              const text = messageText(m);
              if (!text) return null;
              const mine = m.role === "user";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                      mine
                        ? "bg-navy text-white rounded-br-md"
                        : "bg-white text-navy border border-[var(--rule)] rounded-bl-md"
                    }`}
                  >
                    {text}
                  </div>
                </div>
              );
            })}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-[var(--rule)] rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-navy/60 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-navy/40 animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-navy/40 animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-navy/40 animate-bounce" />
                </div>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-600 text-center">
                Es ist ein Fehler aufgetreten. Bitte erneut versuchen.
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-[var(--rule)] bg-white px-3 py-3 shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                rows={1}
                placeholder="Schreibe deine Frage …"
                className="flex-1 min-h-[42px] max-h-32 resize-none rounded-lg border border-[var(--rule)] bg-white px-3 py-2.5 text-sm text-navy placeholder:text-navy/40 outline-none focus:border-[var(--leaf)] focus:ring-2 focus:ring-[var(--leaf)]/20"
                disabled={busy}
              />
              <button
                type="button"
                onClick={submit}
                disabled={busy || !input.trim()}
                aria-label="Senden"
                className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-orange text-white shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 transition"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
                  <path d="M3.4 20.6L22 12 3.4 3.4 3 10l13 2-13 2 .4 6.6z" />
                </svg>
              </button>
            </div>
            <p className="mt-1.5 text-[10px] text-navy/50 text-center">
              Enter zum Senden · Shift+Enter für neue Zeile
            </p>
          </div>
        </div>
      )}
    </>
  );
}
